import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../lib/button/Button';
import {
	DataTypeState,
	PrimitiveTypes,
	DefinitionEntityState,
	EntityType,
	resolveEntityName,
	EntityWithValueState,
	lowercaseFirstLetter,
	toCamelCase,
	resolveEntityCodeName,
	flattenDataTypeAndGroup,
	flattenDataTypesInOrGroup,
	EntityWithDefaultValueState,
	InputMapState,
	IExampleValuesOption,
	ProjectState
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../services/editor';
import { Logger } from '@elyx-code/common-ts-utils';
import { Menu, MenuItem, Tooltip } from '@mui/material';

export async function generateInitialValueForDataType(
	valueOwner: EntityWithDefaultValueState | null,
	dataType: DataTypeState | null,
	project: ProjectState,
	options?: { valueKey?: string }
): Promise<any> {
	if (!!(valueOwner as any)?.codeNativeInitExampleValue) {
		const resolvedValue = (
			await (valueOwner as InputMapState).codeNativeInitExampleValue(
				valueOwner,
				options
			)
		)?.value;

		if (resolvedValue !== undefined) {
			return resolvedValue;
		}
	}

	if (!dataType) return null;

	const flatOrs = dataType.orChildrenGroup || [];

	if (dataType.isList) {
		const initializedValues: any[] = [];

		if (dataType.orChildrenGroup && dataType.orChildrenGroup.length > 0) {
			for (let i = 0; i < dataType.orChildrenGroup.length; i++) {
				const dt = dataType.orChildrenGroup[i];
				const value = await generateInitialValueForDataType(
					null,
					dt,
					project
				);
				initializedValues.push(value);
			}
		}

		if (dataType.andChildrenGroup && dataType.andChildrenGroup.length > 0) {
			for (let i = 0; i < dataType.andChildrenGroup.length; i++) {
				const dt = dataType.andChildrenGroup[i];
				const value = await generateInitialValueForDataType(
					null,
					dt,
					project
				);
				initializedValues.push(value);
			}
		}

		return initializedValues;
	}

	let effectiveDataType = dataType;

	if (flatOrs.length === 1) {
		effectiveDataType = flatOrs[0];
	} else if (!!flatOrs.length) {
		let mostUseful = flatOrs[0];

		while (
			!!mostUseful &&
			[PrimitiveTypes.Null, PrimitiveTypes.Void].includes(
				mostUseful.entity?.name as PrimitiveTypes
			)
		) {
			mostUseful =
				flatOrs.at(flatOrs.indexOf(mostUseful) + 1) || mostUseful;

			if (
				!!mostUseful.entity &&
				(mostUseful.entity.type !== EntityType.PrimitiveEntity ||
					![PrimitiveTypes.Null, PrimitiveTypes.Void].includes(
						mostUseful.entity?.name as PrimitiveTypes
					))
			) {
				break;
			}
		}

		effectiveDataType = mostUseful;
	}

	let flatAnd =
		flattenDataTypeAndGroup(effectiveDataType) || effectiveDataType;

	if (!flatAnd) {
		return null;
	}

	if (flatAnd.foreignKeyRef) {
		const refDataType = flatAnd.foreignKeyRef.getDataType(null);
		return generateInitialValueForDataType(null, refDataType, project);
	}

	if (flatAnd.entity?.type === EntityType.PrimitiveEntity) {
		switch (flatAnd.entity.name) {
			case PrimitiveTypes.String:
			case PrimitiveTypes.UUID:
			case PrimitiveTypes.FileReference:
				return 'Example value';
			case PrimitiveTypes.Number:
				return 1;
			case PrimitiveTypes.Boolean:
				return false;
			case PrimitiveTypes.Date:
				return new Date().toISOString();
			case PrimitiveTypes.KeyValue:
				return {};
			case PrimitiveTypes.Enum:
				return (flatAnd.options as string[])?.[0] ?? '';
			case PrimitiveTypes.Null:
				return null;
			default:
				return '';
		}
	}

	if (flatAnd.entity?.type === EntityType.DefinitionEntity) {
		const result: Record<string, any> = {};
		const defEntity = flatAnd.entity as DefinitionEntityState;

		if (flatAnd.asType) {
			return {
				id: '',
				name: '',
				type: EntityType.DefinitionEntity
			};
		}

		for (let i = 0; i < defEntity.properties.length; i++) {
			const prop = defEntity.properties[i];
			const propDataType = prop.getDataType(null);
			const entityName = resolveEntityName(prop, project);
			const codeName =
				prop.codeName ||
				(resolveEntityCodeName(prop, project) as string) ||
				lowercaseFirstLetter(toCamelCase(entityName));

			result[codeName] = await generateInitialValueForDataType(
				prop,
				propDataType,
				project
			);
		}

		return result;
	}

	if (flatAnd.entity?.type === EntityType.ActionDescriptor) {
		return {
			id: '',
			name: '',
			type: EntityType.ActionDescriptor
		};
	}

	return null;
}

export function getOptionIcon(opt: IExampleValuesOption): string {
	const keyLower = (opt.key || '').toLowerCase();
	const labelLower = (opt.label || '').toLowerCase();

	if (keyLower.includes('default') || labelLower.includes('default')) {
		return 'fa-solid fa-wand-magic-sparkles';
	}
	if (
		keyLower.includes('empty') ||
		labelLower.includes('empty') ||
		keyLower.includes('minimal')
	) {
		return 'fa-solid fa-square-minus';
	}
	if (
		keyLower.includes('full') ||
		labelLower.includes('full') ||
		keyLower.includes('complete')
	) {
		return 'fa-solid fa-cubes';
	}
	if (
		keyLower.includes('sample') ||
		labelLower.includes('sample') ||
		keyLower.includes('example') ||
		keyLower.includes('user')
	) {
		return 'fa-solid fa-flask';
	}
	if (
		keyLower.includes('preset') ||
		labelLower.includes('preset') ||
		keyLower.includes('report') ||
		keyLower.includes('sales')
	) {
		return 'fa-solid fa-table';
	}
	if (keyLower.includes('single') || keyLower.includes('row')) {
		return 'fa-solid fa-list';
	}
	if (keyLower.includes('custom') || labelLower.includes('custom')) {
		return 'fa-solid fa-pen-to-square';
	}
	return 'fa-solid fa-bolt';
}

export interface IInitializeValueButtonProps {
	valueOwner: EntityWithValueState | EntityWithDefaultValueState;
	project: EditorService | ProjectState;
	onInitializeValue: (newValue: any) => void;
	disabled?: boolean;
	size?: 'small' | 'medium';
	color?: 'warning' | 'primary' | 'secondary' | 'success';
	style?: React.CSSProperties;
	buttonText?: string;
}

const InitializeValueButton: React.FC<IInitializeValueButtonProps> = ({
	valueOwner,
	project,
	onInitializeValue,
	disabled = false,
	size = 'small',
	color = 'warning',
	style,
	buttonText = 'Initialize value'
}) => {
	const [exampleValueOptions, setExampleValueOptions] = useState<
		IExampleValuesOption[]
	>([]);
	const [exampleValueMenuAnchor, setExampleValueMenuAnchor] =
		useState<null | HTMLElement>(null);
	const isExampleValueMenuOpen = Boolean(exampleValueMenuAnchor);

	const logicProject = (project as any).logic || project;
	const valueOwnerDataType = valueOwner?.getDataType?.(null) || null;

	const isUntyped =
		!valueOwnerDataType ||
		(!valueOwnerDataType?.entity &&
			!valueOwnerDataType.foreignKeyRef &&
			!valueOwnerDataType.isList &&
			!valueOwnerDataType.orChildrenGroup?.length &&
			!valueOwnerDataType.andChildrenGroup?.length) ||
		valueOwnerDataType?.entity?.name === PrimitiveTypes.Untyped;

	useEffect(() => {
		let isMounted = true;
		const fetchOptions = async () => {
			if (
				valueOwner &&
				typeof (valueOwner as any)
					.codeNativeGetExampleValuesOptions === 'function'
			) {
				try {
					const opts = await (
						valueOwner as any
					).codeNativeGetExampleValuesOptions(valueOwner);
					if (isMounted && Array.isArray(opts)) {
						setExampleValueOptions(opts);
					}
				} catch (err) {
					Logger.error('Failed to fetch example value options', err);
				}
			} else {
				setExampleValueOptions([]);
			}
		};
		fetchOptions();
		return () => {
			isMounted = false;
		};
	}, [valueOwner]);

	const visibleExampleOptions = useMemo(() => {
		return exampleValueOptions.filter((opt) => !opt.hidden);
	}, [exampleValueOptions]);

	const hasMultipleExampleOptions = visibleExampleOptions.length > 1;

	const handleOpenExampleValueMenu = (
		e: React.MouseEvent<HTMLButtonElement>
	) => {
		setExampleValueMenuAnchor(e.currentTarget);
	};

	const handleCloseExampleValueMenu = () => {
		setExampleValueMenuAnchor(null);
	};

	const isBtnDisabled = disabled || isUntyped;

	const tooltipMessage = isUntyped ? (
		<>
			Generates a useful initial value following the required structure
			<br />
			<br />
			(Disabled: Data-type is unconstrained or untyped)
		</>
	) : (
		'Generates a useful initial value following the required structure'
	);

	return (
		<>
			<Tooltip title={tooltipMessage} placement="top">
				<span
					style={{
						display: 'inline-flex',
						flexShrink: 0
					}}
				>
					<Button
						size={size}
						color={color}
						variant="solid"
						disabled={isBtnDisabled}
						iconEnd={
							hasMultipleExampleOptions
								? 'fa-solid fa-chevron-down'
								: undefined
						}
						style={{
							whiteSpace: 'nowrap',
							flexShrink: 0,
							...style
						}}
						onClick={async (
							e: React.MouseEvent<HTMLButtonElement>
						) => {
							if (hasMultipleExampleOptions) {
								handleOpenExampleValueMenu(e);
							} else {
								const singleKey =
									visibleExampleOptions.length === 1
										? visibleExampleOptions[0].key
										: undefined;
								const val = await generateInitialValueForDataType(
									(valueOwner as EntityWithDefaultValueState) ||
										null,
									valueOwnerDataType,
									logicProject,
									singleKey
										? {
												valueKey: singleKey
											}
										: undefined
								);
								onInitializeValue(val);
							}
						}}
					>
						{buttonText}
					</Button>
				</span>
			</Tooltip>

			{hasMultipleExampleOptions && (
				<Menu
					anchorEl={exampleValueMenuAnchor}
					open={isExampleValueMenuOpen}
					onClose={handleCloseExampleValueMenu}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'left'
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'left'
					}}
					PaperProps={{
						elevation: 4,
						style: {
							borderRadius: '10px',
							marginTop: '6px',
							minWidth: '220px',
							padding: '4px 0',
							boxShadow:
								'0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)'
						}
					}}
				>
					{visibleExampleOptions.map((opt) => {
						const isDisabled =
							!!opt.disabledReasons &&
							opt.disabledReasons.length > 0;
						const tooltipText = isDisabled
							? opt.disabledReasons
									.map((r) => r.message || r.code)
									.join('\n')
							: '';
						const iconClass = getOptionIcon(opt);

						const menuItemContent = (
							<MenuItem
								key={opt.key}
								disabled={isDisabled}
								onClick={async () => {
									if (isDisabled) return;
									handleCloseExampleValueMenu();
									const val =
										await generateInitialValueForDataType(
											(valueOwner as EntityWithDefaultValueState) ||
												null,
											valueOwnerDataType,
											logicProject,
											{
												valueKey: opt.key
											}
										);
									onInitializeValue(val);
								}}
								style={{
									fontSize: '13px',
									fontWeight: 500,
									padding: '8px 16px',
									gap: '10px',
									borderRadius: '6px',
									margin: '2px 6px',
									display: 'flex',
									alignItems: 'center'
								}}
							>
								<i
									className={iconClass}
									style={{
										fontSize: '14px',
										width: '16px',
										textAlign: 'center',
										color: isDisabled
											? 'rgba(0, 0, 0, 0.38)'
											: '#ed6c02'
									}}
								/>
								<span style={{ flex: 1 }}>{opt.label}</span>
								{isDisabled && (
									<i
										className="fa-solid fa-circle-info"
										style={{
											fontSize: '12px',
											color: 'rgba(0, 0, 0, 0.4)',
											marginLeft: '8px'
										}}
									/>
								)}
							</MenuItem>
						);

						if (isDisabled && tooltipText) {
							return (
								<Tooltip
									key={opt.key}
									title={
										<div
											style={{
												whiteSpace: 'pre-line',
												fontSize: '12px'
											}}
										>
											{tooltipText}
										</div>
									}
									placement="right"
									arrow
								>
									<span>{menuItemContent}</span>
								</Tooltip>
							);
						}

						return menuItemContent;
					})}
				</Menu>
			)}
		</>
	);
};

export default InitializeValueButton;
